/**
 * CrossGameNudge
 * ──────────────────────────────────────────────────────────────────
 * Auto-appears 2.5 s after a player finishes a daily game.
 *
 * Mode A — NUDGE  (bothComplete = false):
 *   Invites the player to try the other game; shows their streak there.
 *
 * Mode B — BOTH DONE  (bothComplete = true):
 *   Celebrates completing both daily challenges; shows dual streak
 *   badges and social share buttons.
 *
 * Props:
 *   open          boolean
 *   onClose       () => void
 *   fromGame      'perfect-slice' | 'dead-center'
 *   psStreak      number | null
 *   dcStreak      number | null
 *   bothComplete  boolean
 */
import React, { useEffect, useState } from 'react';
import { ArrowRight, Check, Crosshair, Flame, Infinity, LoaderCircle, Scissors, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { usePlatform } from './PlatformContext';
import { trackEvent } from '../lib/analytics';
import { generateBothDoneCard } from '../lib/cubitplayShareCard';

/* ── Analytics slugs ───────────────────────────────────────────────────── */
const GA_SLUG = {
  'perfect-slice': 'perfect_slice',
  'dead-center':   'dead_center',
};

/* ── Per-game nudge config ─────────────────────────────────────────────── */
const NUDGE = {
  'perfect-slice': {
    eyebrow:     'DEAD CENTER · by CubitPlay',
    headline:    ['Nice cut.', 'Now try something harder.'],
    body:        'Dead Center tests pure spatial intuition — one tap per shape, no lines, no ruler, no second chances.',
    streakLabel: 'Dead Center',
    cta:         "PLAY TODAY'S DEAD CENTER",
    targetPath:  '/deadcenter/daily',
    accent:      '#14B8A6',
    accentDark:  '#0F766E',
    Icon:        Crosshair,
  },
  'dead-center': {
    eyebrow:     'PERFECT SLICE · by CubitPlay',
    headline:    ['Nice eye.', 'Can you slice as well as you tap?'],
    body:        'Perfect Slice is all about eyeballing a single straight line — three attempts, your best score counts.',
    streakLabel: 'Perfect Slice',
    cta:         "PLAY TODAY'S PERFECT SLICE",
    targetPath:  '/perfectslice/daily',
    accent:      '#FF8852',
    accentDark:  '#E56F3F',
    Icon:        Scissors,
  },
};

/* ── Shared brand colours ──────────────────────────────────────────────── */
const PS_ACCENT  = '#FF8852';
const PS_DARK    = '#E56F3F';
const DC_ACCENT  = '#14B8A6';
const DC_DARK    = '#0F766E';

/* ── Helpers ───────────────────────────────────────────────────────────── */
function streakLabel(n) {
  if (typeof n !== 'number' || n < 1) return 'Today ✓';
  return `${n}-day streak`;
}

function buildShareText(psStreak, dcStreak) {
  const ps = typeof psStreak === 'number' && psStreak >= 1 ? `${psStreak}-day streak 🔥` : 'done ✓';
  const dc = typeof dcStreak === 'number' && dcStreak >= 1 ? `${dcStreak}-day streak 🔥` : 'done ✓';
  return `Daily double — both CubitPlay challenges done!\n✂️ Perfect Slice: ${ps}\n◎ Dead Center: ${dc}\ncubitplay.com`;
}

/* ── Component ─────────────────────────────────────────────────────────── */
export function CrossGameNudge({
  open,
  onClose,
  fromGame,
  psStreak      = null,
  dcStreak      = null,
  bothComplete  = false,
  onPlayEndless = null,
}) {
  const { navigate }   = usePlatform();
  const cfg            = NUDGE[fromGame];
  const [copied,        setCopied]        = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(false);

  const fromSlug = GA_SLUG[fromGame] || fromGame || 'unknown';
  const toSlug   = cfg ? (GA_SLUG[cfg.targetPath.startsWith('/dead') ? 'dead-center' : 'perfect-slice'] || 'unknown') : 'unknown';
  /* In nudge mode, the streak for the TARGET game */
  const targetStreak   = fromGame === 'perfect-slice' ? dcStreak : psStreak;
  const hasTargetStreak = typeof targetStreak === 'number' && targetStreak >= 2;

  /* ── Analytics: nudge_shown ── */
  useEffect(() => {
    if (!open) return;
    trackEvent('nudge_shown', {
      from_game:     fromSlug,
      to_game:       bothComplete ? 'both' : toSlug,
      target_streak: targetStreak ?? 0,
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cfg && !bothComplete) return null;

  /* ── Handlers (nudge mode) ── */
  const handlePlay = () => {
    trackEvent('nudge_cta_click', { from_game: fromSlug, to_game: toSlug, target_streak: targetStreak ?? 0 });
    onClose();
    navigate(cfg.targetPath);
  };

  const handleDismiss = () => {
    trackEvent('nudge_dismiss', { from_game: fromSlug, to_game: bothComplete ? 'both' : toSlug, target_streak: targetStreak ?? 0 });
    onClose();
  };

  /* ── Share handlers (both-done mode) ── */
  const shareText = buildShareText(psStreak, dcStreak);

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
    trackEvent('nudge_cta_click', { from_game: fromSlug, to_game: 'whatsapp', target_streak: 0 });
  };

  const openTwitter = () => {
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent('https://cubitplay.com')}`,
      '_blank', 'noopener',
    );
    trackEvent('nudge_cta_click', { from_game: fromSlug, to_game: 'twitter', target_streak: 0 });
  };

  const handleNativeShare = async () => {
    trackEvent('nudge_cta_click', { from_game: fromSlug, to_game: 'native_share', target_streak: 0 });
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: 'CubitPlay Daily Double', text: shareText, url: 'https://cubitplay.com' });
      } catch { /* user cancelled */ }
    } else {
      // Clipboard fallback
      try {
        await navigator.clipboard.writeText(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      } catch { /* no clipboard */ }
    }
  };

  /* ── Instagram Stories: generate card → native share / download ── */
  const openInstagram = async () => {
    if (isCardLoading) return;
    trackEvent('nudge_cta_click', { from_game: fromSlug, to_game: 'instagram', target_streak: 0 });
    setIsCardLoading(true);
    try {
      const { blob, filename } = await generateBothDoneCard({ psStreak, dcStreak });
      // Mobile: try Web Share API with file (opens Instagram, etc.)
      if (typeof navigator.share === 'function' && typeof navigator.canShare === 'function') {
        const file = new File([blob], filename, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'CubitPlay Daily Double' });
          return;
        }
      }
      // Desktop fallback: download PNG
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast('Saved! Open Instagram \u2192 Create Story \u2192 select from Photos', { duration: 5500 });
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('Could not generate card. Try again.');
    } finally {
      setIsCardLoading(false);
    }
  };

  /* ════════════════════════════════════════════════════════════════════
     MODE B — BOTH DONE
  ════════════════════════════════════════════════════════════════════ */
  if (bothComplete) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
        <DialogContent
          className="cross-game-nudge nudge-both-done"
          data-testid="cross-game-nudge-modal"
          aria-describedby="nudge-both-body"
        >
          <DialogTitle className="sr-only">Daily double complete</DialogTitle>

          {/* Split accent bar: PS orange | DC teal */}
          <div className="nudge-accent-bar nudge-accent-dual" aria-hidden="true" />

          {/* Eyebrow */}
          <p className="nudge-eyebrow nudge-eyebrow-dual" data-testid="cross-game-nudge-eyebrow">
            <Scissors size={12} style={{ color: PS_ACCENT }} aria-hidden="true" />
            PERFECT SLICE
            <span className="nudge-eyebrow-sep" aria-hidden="true">·</span>
            <Crosshair size={12} style={{ color: DC_ACCENT }} aria-hidden="true" />
            DEAD CENTER
          </p>

          {/* Main headline */}
          <h2 className="nudge-headline" data-testid="cross-game-nudge-headline">
            Daily double.<br />
            <span className="nudge-headline-sub">What's next?</span>
          </h2>

          {/* Body */}
          <p className="nudge-body" id="nudge-both-body" data-testid="cross-game-nudge-body">
            You've completed both daily challenges today.
          </p>

          {/* Dual streak showcase */}
          <div className="nudge-streak-showcase" data-testid="cross-game-nudge-streak-showcase">
            {/* Perfect Slice row */}
            <div
              className="nudge-streak-row"
              style={{ borderColor: PS_ACCENT + '44', background: PS_ACCENT + '0D' }}
              data-testid="cross-game-nudge-ps-streak-row"
            >
              <span className="nudge-streak-row-label">
                <Scissors size={14} style={{ color: PS_DARK }} aria-hidden="true" />
                Perfect Slice
              </span>
              <span className="nudge-streak-row-value" style={{ color: PS_DARK }} data-testid="cross-game-nudge-ps-streak-value">
                {typeof psStreak === 'number' && psStreak >= 1 && (
                  <Flame size={13} aria-hidden="true" />
                )}
                {streakLabel(psStreak)}
              </span>
            </div>
            {/* Dead Center row */}
            <div
              className="nudge-streak-row"
              style={{ borderColor: DC_ACCENT + '44', background: DC_ACCENT + '0D' }}
              data-testid="cross-game-nudge-dc-streak-row"
            >
              <span className="nudge-streak-row-label">
                <Crosshair size={14} style={{ color: DC_DARK }} aria-hidden="true" />
                Dead Center
              </span>
              <span className="nudge-streak-row-value" style={{ color: DC_DARK }} data-testid="cross-game-nudge-dc-streak-value">
                {typeof dcStreak === 'number' && dcStreak >= 1 && (
                  <Flame size={13} aria-hidden="true" />
                )}
                {streakLabel(dcStreak)}
              </span>
            </div>
          </div>

          {/* Divider + Endless CTA */}
          <div className="nudge-rule" aria-hidden="true" />

          {onPlayEndless && (
            <button
              className="nudge-cta-btn"
              style={{
                background: 'linear-gradient(135deg, #FF8852 0%, #14B8A6 100%)',
                '--nudge-hover': '#0F766E',
              }}
              data-testid="cross-game-nudge-endless-button"
              onClick={() => {
                trackEvent('nudge_cta_click', { from_game: fromSlug, to_game: 'endless', target_streak: 0 });
                onClose();
                onPlayEndless();
              }}
            >
              <Infinity size={17} aria-hidden="true" /> PLAY ENDLESS MODE
            </button>
          )}

          <p className="nudge-share-label" data-testid="cross-game-nudge-share-label" style={{ marginTop: onPlayEndless ? '14px' : undefined }}>
            <Share2 size={13} aria-hidden="true" /> Or share your result
          </p>

          <div className="nudge-share-row" data-testid="cross-game-nudge-share-row">
            {/* WhatsApp */}
            <button
              className="nudge-share-btn nudge-share-whatsapp"
              data-testid="cross-game-nudge-share-whatsapp"
              onClick={openWhatsApp}
              aria-label="Share on WhatsApp"
            >
              {/* WhatsApp logo */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </button>

            {/* X / Twitter */}
            <button
              className="nudge-share-btn nudge-share-x"
              data-testid="cross-game-nudge-share-x"
              onClick={openTwitter}
              aria-label="Share on X (Twitter)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
              </svg>
              Post on X
            </button>

            {/* Native / Copy */}
            <button
              className="nudge-share-btn nudge-share-native"
              data-testid="cross-game-nudge-share-native"
              onClick={handleNativeShare}
              aria-label="Share or copy result"
            >
              {copied
                ? <Check size={15} aria-hidden="true" />
                : <Share2 size={15} aria-hidden="true" />
              }
              {copied ? 'Copied!' : 'Share'}
            </button>
          </div>

          {/* Instagram Stories card */}
          <button
            className="nudge-share-instagram"
            data-testid="cross-game-nudge-share-instagram"
            onClick={openInstagram}
            disabled={isCardLoading}
            aria-label="Save card for Instagram Story"
          >
            {isCardLoading ? (
              <>
                <LoaderCircle size={15} className="spin" aria-hidden="true" />
                Generating…
              </>
            ) : (
              <>
                {/* Instagram camera icon */}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <circle cx="12" cy="12" r="4.3"/>
                  <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"/>
                </svg>
                Save for Instagram Story
              </>
            )}
          </button>

          {/* Dismiss */}
          <button
            className="nudge-dismiss-btn"
            data-testid="cross-game-nudge-dismiss-button"
            onClick={handleDismiss}
          >
            {onPlayEndless ? 'Maybe later' : 'See you tomorrow'}
          </button>
        </DialogContent>
      </Dialog>
    );
  }

  /* ════════════════════════════════════════════════════════════════════
     MODE A — NUDGE
  ════════════════════════════════════════════════════════════════════ */
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent
        className="cross-game-nudge"
        data-testid="cross-game-nudge-modal"
        aria-describedby="nudge-body"
      >
        <DialogTitle className="sr-only">Play {cfg.eyebrow}</DialogTitle>

        {/* Accent bar */}
        <div className="nudge-accent-bar" style={{ background: cfg.accent }} aria-hidden="true" />

        {/* Eyebrow */}
        <p className="nudge-eyebrow" style={{ color: cfg.accentDark }} data-testid="cross-game-nudge-eyebrow">
          <cfg.Icon size={13} aria-hidden="true" />
          {cfg.eyebrow}
        </p>

        {/* Headline */}
        <h2 className="nudge-headline" data-testid="cross-game-nudge-headline">
          {cfg.headline[0]}<br />{cfg.headline[1]}
        </h2>

        {/* Body */}
        <p className="nudge-body" id="nudge-body" data-testid="cross-game-nudge-body">
          {cfg.body}
        </p>

        {/* Streak badge (only when targetStreak ≥ 2) */}
        {hasTargetStreak && (
          <div
            className="nudge-streak-badge"
            style={{ borderColor: cfg.accent + '55', background: cfg.accent + '11' }}
            data-testid="cross-game-nudge-streak-badge"
          >
            <Flame size={15} style={{ color: cfg.accent, flexShrink: 0 }} aria-hidden="true" />
            <span data-testid="cross-game-nudge-streak-text">
              You have a{' '}
              <strong style={{ color: cfg.accentDark }}>
                {targetStreak}-day {cfg.streakLabel} streak
              </strong>
              {' '}— don&rsquo;t break it.
            </span>
          </div>
        )}

        {/* Divider */}
        <div className="nudge-rule" aria-hidden="true" />

        {/* CTA */}
        <button
          className="nudge-cta-btn"
          style={{ background: cfg.accent, '--nudge-hover': cfg.accentDark }}
          data-testid="cross-game-nudge-play-button"
          onClick={handlePlay}
        >
          {cfg.cta}
          <ArrowRight size={16} aria-hidden="true" />
        </button>

        {/* Dismiss */}
        <button
          className="nudge-dismiss-btn"
          data-testid="cross-game-nudge-dismiss-button"
          onClick={handleDismiss}
        >
          Maybe later
        </button>
      </DialogContent>
    </Dialog>
  );
}
